import { useRouteError } from 'react-router-dom';
import logoblack from './ui/Logo/logo-black.svg';
import logowhite from './ui/Logo/logo-white.svg';

interface StandaloneErrorPageProps {
  title?: string;
  message?: string;
  showHomeButton?: boolean;
  showReloadButton?: boolean;
}

interface RouteError {
  status?: number;
  statusText?: string;
  message?: string;
  data?: unknown;
}

const translations: Record<string, Record<string, string>> = {
  'en': {
    page_not_found: 'Page Not Found',
    page_not_found_message: "The page you're looking for doesn't exist.",
    something_went_wrong: 'Something went wrong',
    unexpected_error_message: "We're sorry, but something unexpected happened.",
    go_home: 'Go Home',
    reload_page: 'Reload Page'
  },
  'en-US': {
    page_not_found: 'Page Not Found',
    page_not_found_message: "The page you're looking for doesn't exist.",
    something_went_wrong: 'Something went wrong',
    unexpected_error_message: "We're sorry, but something unexpected happened.",
    go_home: 'Go Home',
    reload_page: 'Reload Page'
  },
  'ru': {
    page_not_found: 'Страница не найдена',
    page_not_found_message: 'Страница, которую вы ищете, не существует.',
    something_went_wrong: 'Что-то пошло не так',
    unexpected_error_message: 'Извините, произошла неожиданная ошибка.',
    go_home: 'На главную',
    reload_page: 'Перезагрузить страницу'
  },
  'es': {
    page_not_found: 'Página no encontrada',
    page_not_found_message: 'La página que buscas no existe.',
    something_went_wrong: 'Algo salió mal',
    unexpected_error_message: 'Lo sentimos, pero algo inesperado ha ocurrido.',
    go_home: 'Ir al inicio',
    reload_page: 'Recargar página'
  },
  'pt': {
    page_not_found: 'Página Não Encontrada',
    page_not_found_message: 'A página que você está procurando não existe.',
    something_went_wrong: 'Algo deu errado',
    unexpected_error_message: 'Desculpe, mas algo inesperado aconteceu.',
    go_home: 'Ir para Início',
    reload_page: 'Recarregar Página'
  },
  'de': {
    page_not_found: 'Seite nicht gefunden',
    page_not_found_message: 'Die gesuchte Seite existiert nicht.',
    something_went_wrong: 'Etwas ist schiefgelaufen',
    unexpected_error_message: 'Es tut uns leid, aber etwas Unerwartetes ist passiert.',
    go_home: 'Zur Startseite',
    reload_page: 'Seite neu laden'
  }
};

// Simple language parser matching the app's logic
const parseLang = (lang: string): string => {
  const normalized = lang.toLowerCase();
  if (normalized === 'ru' || normalized === 'ru-ru') return 'ru';
  if (normalized === 'de' || normalized.startsWith('de')) return 'de';
  if (normalized === 'es' || normalized.startsWith('es')) return 'es';
  if (normalized === 'pt' || normalized.startsWith('pt')) return 'pt';
  if (normalized === 'en-us' || normalized === 'en') return 'en';
  return 'en';
};

export function StandaloneErrorPage({
  title,
  message,
  showHomeButton = true,
  showReloadButton = false
}: StandaloneErrorPageProps) {
  // Get route error information from React Router
  const routeError = useRouteError() as RouteError | undefined;

  // Determine if this is a 404 error
  const is404 = routeError?.status === 404;

  // Get theme preference
  let theme = 'light';
  if (typeof window !== 'undefined') {
    const storedTheme = localStorage.getItem('theme-preference');
    if (storedTheme === 'dark' || storedTheme === 'light') {
      theme = storedTheme;
    } else if (storedTheme === 'system') {
      theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
  }

  const isDark = theme === 'dark';

  // Get language using the same logic as the main app
  let lang = 'en';

  if (typeof window !== 'undefined') {
    // Method 1: Check i18next stored language (this is what i18next uses after language change)
    const i18nLang = localStorage.getItem('i18nextLng');
    if (i18nLang && translations[i18nLang]) {
      lang = i18nLang;
    } else {
      // Method 2: Check our app's stored language preference
      try {
        const storedLang = localStorage.getItem('lang');
        if (storedLang) {
          const parsedStoredLang = JSON.parse(storedLang);
          lang = parseLang(parsedStoredLang);
        }
      } catch (e) {
        // Fall back to browser language
      }

      // Method 3: Use browser language as last resort
      if (lang === 'en' && navigator.language) {
        const browserLang = navigator.language.split('-')[0].toLowerCase();
        if (browserLang === 'ru' || browserLang === 'en') {
          lang = browserLang;
        }
      }
    }
  }

  const t = translations[lang] || translations['en'];

  // Determine title and message based on error type
  let displayTitle: string;
  let displayMessage: string;

  if (title) {
    displayTitle = title;
  } else if (is404) {
    displayTitle = t.page_not_found;
  } else {
    displayTitle = t.something_went_wrong;
  }

  if (message) {
    displayMessage = message;
  } else if (is404) {
    displayMessage = t.page_not_found_message;
  } else {
    displayMessage = t.unexpected_error_message;
  }
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: isDark ? '#111827' : 'white',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '32px',
      textAlign: 'center'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '800px'
      }}>
        <img
          src={isDark ? logowhite : logoblack}
          alt="transcriptor.ai"
          width={150}
          style={{
            margin: '0 auto 48px',
            display: 'block'
          }}
        />
        <h1 style={{
          fontSize: '32px',
          fontWeight: 600,
          color: isDark ? '#f9fafb' : '#111827',
          margin: '0 0 24px',
          lineHeight: 1.2
        }}>
          {displayTitle}
        </h1>
        <p style={{
          fontSize: '18px',
          color: isDark ? '#9ca3af' : '#6b7280',
          margin: '0 0 48px',
          lineHeight: 1.6,
          maxWidth: '600px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          {displayMessage}
        </p>
        <div style={{
          display: 'flex',
          gap: '16px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {showHomeButton && (
            <a
              href="/"
              style={{
                display: 'inline-block',
                padding: '14px 32px',
                backgroundColor: '#3b82f6',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '16px',
                fontWeight: 500,
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
            >
              {t.go_home}
            </a>
          )}
          {showReloadButton && (
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '14px 32px',
                backgroundColor: isDark ? '#374151' : '#e5e7eb',
                color: isDark ? '#e5e7eb' : '#374151',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = isDark ? '#4b5563' : '#d1d5db'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = isDark ? '#374151' : '#e5e7eb'}
            >
              {t.reload_page}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
